import { API_BASE_URL, apiFetch, readCsrfToken } from '@/lib/api/http';

/** CSV 3段階API（指示書 §8）: upload（検証+プレビュー）→ confirm（理由必須）→ get。 */
export interface CsvRowResult {
  row_no: number;
  action: 'create' | 'update' | 'no_change' | 'error';
  member_id: string | null;
  errors: string[];
  warnings: string[];
  data: Record<string, unknown>;
}

/**
 * 取込プレビューの「判定」列の表示名（D-086）。
 * 会員・イベント・社長アンケート・席次で同じ文言を使う（画面ごとに違うと混乱するため）。
 */
export const CSV_ACTION_LABELS: Record<CsvRowResult['action'], string> = {
  create: '新しく登録',
  update: '内容を更新',
  no_change: '変更なし',
  error: 'エラー（取り込めません）',
};

export interface CsvPreviewSummary {
  total_rows: number;
  create_count: number;
  update_count: number;
  no_change_count: number;
  error_count: number;
}

export interface CsvJob {
  id: string;
  kind: string;
  status: 'uploaded' | 'validated' | 'confirmed' | 'failed' | 'cancelled';
  original_filename: string | null;
  uploaded_file_sha256: string;
  summary: CsvPreviewSummary | null;
  rows: CsvRowResult[];
  confirmed_at: string | null;
  confirm_reason: string | null;
  created_at: string;
  event_id: string | null;
}

async function uploadCsv(
  path: string,
  file: File,
  extraFields?: Record<string, string>,
): Promise<CsvJob> {
  const form = new FormData();
  form.append('file', file);
  if (extraFields) {
    for (const [key, value] of Object.entries(extraFields)) form.append(key, value);
  }
  const csrf = readCsrfToken();

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: csrf ? { 'X-CSRF-Token': csrf } : {},
    body: form,
  });
  const body: unknown = await res.json();
  if (!res.ok) {
    const error = (body as { error?: { code: string; message: string } }).error;
    throw new Error(error?.message ?? '取込に失敗しました。');
  }
  return body as CsvJob;
}

export function uploadMemberCsv(file: File): Promise<CsvJob> {
  return uploadCsv('/csv/member-import/upload', file);
}

export function confirmMemberCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/member-import/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function fetchMemberCsvJob(jobId: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/member-import/${jobId}`);
}

export function memberExportUrl(): string {
  return `${API_BASE_URL}/csv/member-export`;
}

// --- イベント一括作成CSV（指示書 §4・§11 / DECISIONS D-014） -----------------
export function uploadEventCsv(file: File): Promise<CsvJob> {
  return uploadCsv('/csv/event-import/upload', file);
}

export function confirmEventCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/event-import/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

// --- 1on1組み合わせCSV（ヒアリング⑥-9 / DECISIONS D-273） -----------------
export function uploadOneOnOneCsv(file: File): Promise<CsvJob> {
  return uploadCsv('/csv/oneonone-events/upload', file);
}

export function confirmOneOnOneCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/oneonone-events/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

/** サクセス面談組み合わせCSV（D-274） */
export function uploadSuccessMeetingCsv(file: File): Promise<CsvJob> {
  return uploadCsv('/csv/success-meeting-events/upload', file);
}

export function confirmSuccessMeetingCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/success-meeting-events/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

// --- 社長アンケートCSV（csv-contracts.md 縦持ち5段階 / §12 M7） --------------
export function uploadPresidentCsv(file: File): Promise<CsvJob> {
  return uploadCsv('/csv/president-import/upload', file);
}

export function confirmPresidentCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/president-import/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function presidentExportUrl(): string {
  return `${API_BASE_URL}/csv/president-export`;
}

// --- Meet参加ログCSV（implementation-spec §8 / DECISIONS D-031 / §12 M9） ---
export function uploadMeetLogCsv(eventId: string, file: File): Promise<CsvJob> {
  return uploadCsv('/csv/meet-log-import/upload', file, { event_id: eventId });
}

export function confirmMeetLogCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/meet-log-import/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

// --- 席次CSV（docs/csv-contracts.md「席次 出力」/ DECISIONS D-032 / §12 M9） --
export function uploadSeatingCsv(eventId: string, file: File): Promise<CsvJob> {
  return uploadCsv('/csv/seating-import/upload', file, { event_id: eventId });
}

export function confirmSeatingCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/seating-import/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export function seatingExportUrl(eventId: string): string {
  return `${API_BASE_URL}/csv/seating-export?event_id=${eventId}`;
}

// --- 採点結果CSV（csv-contracts.md「採点 結果取込」/ DECISIONS D-035・D-036 / §12 M9） --
/** 取込先の月はCSVの period 列で決まる（画面で月を選び直す必要はない / D-097） */
export function uploadScoreResultCsv(file: File): Promise<CsvJob> {
  return uploadCsv('/csv/score-result-import/upload', file);
}

export function confirmScoreResultCsv(jobId: string, reason: string): Promise<CsvJob> {
  return apiFetch<CsvJob>(`/csv/score-result-import/${jobId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

/** 取込用サンプルCSV（D-111）。見出しは日本語。英語列名のファイルも取り込める（D-283） */
export function csvSampleUrl(
  kind: 'member' | 'event' | 'president' | 'score' | 'meet-log' | 'oneonone' | 'success-meeting',
): string {
  return `${API_BASE_URL}/csv/samples/${kind}`;
}
